import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatBRL } from "@/lib/format";
import { Copy, Check, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function OrderPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      return data;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(async () => {
      const o = await fetchOrder();
      if (o && (o.status === "PAID" || o.status === "REFUNDED")) {
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [id]);

  const copyPayload = () => {
    if (!order?.pix_payload) return;
    navigator.clipboard.writeText(order.pix_payload);
    setCopied(true);
    toast.success("Código PIX copiado!");
    setTimeout(() => setCopied(false), 2500);
  };

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1BB0CE]" />
        <p className="text-sm text-[#666] mt-4">Carregando pedido...</p>
      </div>
    );
  }

  const isPaid = order.status === "PAID" || order.status === "SHIPPED";

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE] mb-3">
          PEDIDO #{order.id.slice(0, 8).toUpperCase()}
        </div>
        <h1 className="font-quicksand text-4xl font-extrabold text-[#3d3d3d]">
          {order.status === "SHIPPED" ? "Pedido enviado!" : isPaid ? "Pagamento confirmado!" : "Pague com PIX"}
        </h1>
      </div>

      {isPaid ? (
        <div className="card-soft p-10 sm:p-14 text-center" data-testid="order-paid-confirmation">
          <div className="w-20 h-20 rounded-full bg-gp-gradient mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2} />
          </div>
          <h2 className="font-quicksand text-2xl font-extrabold text-[#3d3d3d]">
            {order.status === "SHIPPED" ? "Pedido a caminho!" : "Recebemos seu pagamento!"}
          </h2>
          <p className="text-sm text-[#666] mt-3 max-w-md mx-auto leading-relaxed">
            Seu pedido <span className="font-quicksand font-bold text-[#3d3d3d]">{order.id.slice(0, 8).toUpperCase()}</span> foi confirmado.
          </p>
          {order.status === "SHIPPED" ? (
            <div className="mt-6 p-6 bg-sky-50 border border-sky-100 rounded-2xl max-w-md mx-auto text-left font-quicksand">
              <span className="font-bold text-[#0369A1] block text-sm mb-2">🚚 Rastreamento da Entrega</span>
              <div className="text-xs text-[#666]">
                Código de rastreio: <span className="font-bold text-[#3d3d3d] font-mono">{order.tracking_code}</span>
              </div>
              <a
                href={`https://www.melhorrastreio.com.br/app/loggi/${order.tracking_code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gp w-full mt-4 text-center text-xs font-bold py-2 block"
              >
                Acompanhar no Melhor Rastreio
              </a>
            </div>
          ) : (
            <p className="text-sm text-[#666] mt-3 max-w-md mx-auto leading-relaxed">
              Enviamos um e-mail de confirmação para você. O código de rastreio será enviado assim que o seu pedido for despachado.
            </p>
          )}
          <Link to="/loja" data-testid="back-to-shop" className="btn-gp mt-8 inline-flex items-center gap-2">
            VOLTAR À LOJA
          </Link>
        </div>

      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-soft p-8 sm:p-10 flex flex-col items-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-full mb-6">
              <Clock className="w-4 h-4 text-orange-500 animate-pulse" />
              <span className="text-xs font-quicksand font-bold text-orange-600 uppercase tracking-wider">
                AGUARDANDO PAGAMENTO
              </span>
            </div>

            {order.pix_qr_image && (
              <div className="bg-white border-4 border-[#1BB0CE] rounded-3xl p-4 inline-block" data-testid="pix-qr-container">
                <img src={`data:image/png;base64,${order.pix_qr_image}`} alt="QR Code PIX" className="w-64 h-64" />
              </div>
            )}

            <p className="text-sm text-[#666] mt-6 text-center max-w-xs">
              Abra o app do seu banco, escolha pagar via PIX → QR Code e escaneie a imagem acima.
            </p>
          </div>

          <div className="card-soft p-8 sm:p-10">
            <div>
              <div className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#999]">Valor total</div>
              <div className="font-quicksand text-4xl font-extrabold text-gradient-gp mt-1" data-testid="order-total">
                {formatBRL(order.total)}
              </div>
            </div>

            <div className="mt-8">
              <div className="text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider mb-2">
                PIX copia & cola
              </div>
              <div className="bg-[#fafbfc] border border-[#f1f1f3] rounded-2xl p-4 break-all text-xs leading-relaxed text-[#666] font-mono" data-testid="pix-payload-text">
                {order.pix_payload}
              </div>
              <button onClick={copyPayload} data-testid="copy-pix-button" className="btn-gp w-full mt-4">
                {copied ? <><Check className="w-4 h-4" /> COPIADO!</> : <><Copy className="w-4 h-4" /> COPIAR CÓDIGO PIX</>}
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-[#f1f1f3]">
              <div className="text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider mb-3">
                ITENS DO PEDIDO
              </div>
              <ul className="space-y-2 text-sm">
                {order.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span className="text-[#666]">{i.quantity}x {i.name}</span>
                    <span className="font-quicksand font-bold text-[#3d3d3d]">{formatBRL(i.price * i.quantity)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs font-quicksand text-[#999]">
              <Loader2 className="w-3 h-3 animate-spin" />
              Verificando pagamento automaticamente...
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
